import { createMemo, createSignal, For, onMount, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { SearchInput } from '../components/SearchInput';
import ItemStackView from '../components/ItemStackView';
import MinecraftText from '../components/MinecraftText';
import Text from '../components/Text';
import { RelativeTime } from '../components/RelativeTime';
import { Skeleton } from '../components/Skeleton';
import { Citybuild } from '../models/Citybuild';
import { fromMaterialItem } from '../services/itemRefAdapters';
import { formatInteger } from '../services/numberFormat';
import styles from './ShopStockWatchlist.module.css';
import { firstError } from '../api/envelope';

const getCitybuildLabel = (cb: number): string => Citybuild[cb] ?? '-';

type ChangeKind = 'Listed' | 'Delisted' | 'Raised' | 'Lowered';

interface WatchedBot {
    id: string;
    uuid: string;
    name: string;
    citybuild: number;
    fetchedAt: string;
    nextRefreshAtUtc: string | null;
    itemCount: number;
}

interface PriceChange {
    id: string;
    botId: string;
    botName: string;
    citybuild: number;
    itemId: string;
    displayName: string;
    materialName: string;
    materialSubId: number;
    enchantmentsJson: string;
    loreJson: string;
    repairCost: number;
    stackSize: number;
    amount: number;
    oldPrice: number | null;
    newPrice: number | null;
    kind: ChangeKind;
    detectedAt: string;
    warehouseCount: number | null;
}

interface WatchlistData {
    bots: WatchedBot[];
    changes: PriceChange[];
    totalChanges: number;
}

const PAGE_SIZE = 50;

export default function ShopStockWatchlist() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [data, setData] = createSignal<WatchlistData>({ bots: [], changes: [], totalChanges: 0 });
    const [page, setPage] = createSignal(1);
    const [initialLoading, setInitialLoading] = createSignal(true);
    const [newUsername, setNewUsername] = createSignal('');
    const [adding, setAdding] = createSignal(false);
    const [botFilter, setBotFilter] = createSignal<string>('');
    const [sideFilter, setSideFilter] = createSignal<'' | 'sell' | 'buy'>('');
    const [search, setSearch] = createSignal('');

    // The shop quotes its buy offers as negative prices, the same convention the stock tooltips and the
    // compare view use. Whichever end of the change exists carries the sign.
    const sideOf = (change: PriceChange): 'sell' | 'buy' =>
        (change.newPrice ?? change.oldPrice ?? 0) < 0 ? 'buy' : 'sell';

    const stripColors = (s: string) => s.replace(/§[0-9a-fk-or]/gi, '');

    const formatPrice = (p: number) =>
        `$${Math.abs(p).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const load = async (targetPage: number) => {
        const res = await api.shopStock.getWatchlist({ page: targetPage, pageSize: PAGE_SIZE });
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_WATCH_LOAD_FAILED'));
            return;
        }
        setData(res.data.value as unknown as WatchlistData);
        setPage(targetPage);
    };

    onMount(async () => {
        try {
            await load(1);
        } finally {
            setInitialLoading(false);
        }
    });

    const addWatch = async (e: Event) => {
        e.preventDefault();
        const name = newUsername().trim();
        if (!name) return;
        setAdding(true);
        const res = await api.shopStock.addWatch({ username: name });
        setAdding(false);
        if (isOk(res.data)) {
            setNewUsername('');
            await load(1);
            toast?.info(t('SHOP_STOCK_WATCH_ADDED'));
        } else {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_WATCH_ADD_FAILED'));
        }
    };

    const removeWatch = async (bot: WatchedBot) => {
        if (!await showConfirm(`${t('SHOP_STOCK_WATCH_REMOVE_CONFIRM')} "${bot.name}"?`, true)) return;
        const res = await api.shopStock.removeWatch(bot.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SHOP_STOCK_WATCH_REMOVE_FAILED'));
            return;
        }
        await load(1);
    };

    const visibleChanges = createMemo(() => {
        const bot = botFilter();
        const side = sideFilter();
        const query = search().trim().toLowerCase();
        return data().changes.filter(change => {
            if (bot && change.botId !== bot) return false;
            if (side && sideOf(change) !== side) return false;
            if (!query) return true;
            return stripColors(change.displayName).toLowerCase().includes(query)
                || change.materialName.toLowerCase().includes(query);
        });
    });

    const pageCount = createMemo(() => Math.max(1, Math.ceil(data().totalChanges / PAGE_SIZE)));

    const kindClass = (kind: ChangeKind) => {
        if (kind === 'Raised') return styles.kindRaised;
        if (kind === 'Lowered') return styles.kindLowered;
        if (kind === 'Listed') return styles.kindListed;
        return styles.kindDelisted;
    };

    const kindLabel = (kind: ChangeKind) => t(`SHOP_STOCK_WATCH_KIND_${kind.toUpperCase()}`);

    const deltaPercent = (change: PriceChange) => {
        if (change.oldPrice == null || change.newPrice == null) return null;
        const from = Math.abs(change.oldPrice);
        if (from === 0) return null;
        const ratio = (Math.abs(change.newPrice) - from) / from;
        const percent = (ratio * 100).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        return `${ratio > 0 ? '+' : ''}${percent}%`;
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="SHOP_STOCK_WATCH_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {data().totalChanges} <Text val="SHOP_STOCK_WATCH_CHANGES" />
                </span>
            </div>
            <p class={styles.intro}><Text val="SHOP_STOCK_WATCH_INTRO" /></p>

            <form onSubmit={addWatch} class={`fcard ${styles.addForm}`}>
                <input
                    type="text"
                    placeholder={t('SHOP_STOCK_USERNAME_PLACEHOLDER')}
                    value={newUsername()}
                    onInput={(e) => setNewUsername(e.currentTarget.value)}
                    disabled={adding()}
                />
                <button class="btn btn-primary" disabled={adding() || !newUsername().trim()}>
                    <Show when={adding()} fallback={<><i class="fa-solid fa-eye" /> <Text val="SHOP_STOCK_WATCH_ADD" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="SHOP_STOCK_WATCH_ADDING" />
                    </Show>
                </button>
            </form>

            <div class={styles.toolbar}>
                <SearchInput
                    value={search()}
                    onInput={setSearch}
                    placeholder={t('SHOP_STOCK_WATCH_SEARCH_PLACEHOLDER')}
                />
                <select
                    class={styles.filterSelect}
                    value={botFilter()}
                    onChange={(e) => setBotFilter(e.currentTarget.value)}
                    title={t('SHOP_STOCK_WATCH_FILTER_BOT')}
                >
                    <option value="">{t('SHOP_STOCK_WATCH_ALL_BOTS')}</option>
                    <For each={data().bots}>
                        {(bot) => <option value={bot.id}>{bot.name}</option>}
                    </For>
                </select>
                <select
                    class={styles.filterSelect}
                    value={sideFilter()}
                    onChange={(e) => setSideFilter(e.currentTarget.value as '' | 'sell' | 'buy')}
                    title={t('SHOP_STOCK_WATCH_FILTER_SIDE')}
                >
                    <option value="">{t('SHOP_STOCK_WATCH_ALL_SIDES')}</option>
                    <option value="sell">{t('SHOP_STOCK_WATCH_SIDE_SELL')}</option>
                    <option value="buy">{t('SHOP_STOCK_WATCH_SIDE_BUY')}</option>
                </select>
            </div>

            <Show when={!initialLoading()} fallback={
                <div class={styles.feed}>
                    <For each={[0, 1, 2, 3, 4, 5]}>
                        {() => (
                            <div class={`fcard ${styles.changeRow}`}>
                                <Skeleton variant="rect" width={40} height={40} />
                                <Skeleton variant="text" width={220} />
                                <Skeleton variant="text" width={120} />
                            </div>
                        )}
                    </For>
                </div>
            }>
                <Show when={data().bots.length > 0} fallback={
                    <div class={`fcard ${styles.emptyState}`}>
                        <i class="fa-solid fa-eye-slash" />
                        <p><Text val="SHOP_STOCK_WATCH_EMPTY" /></p>
                    </div>
                }>
                    <Show when={visibleChanges().length > 0} fallback={
                        <div class={`fcard ${styles.emptyState}`}>
                            <i class="fa-solid fa-wave-square" />
                            <p><Text val={search() || botFilter() ? 'SHOP_STOCK_WATCH_NO_MATCHES' : 'SHOP_STOCK_WATCH_NO_CHANGES'} /></p>
                        </div>
                    }>
                        <div class={styles.feed}>
                            <For each={visibleChanges()}>
                                {(change) => (
                                    <div class={`fcard ${styles.changeRow}`}>
                                        <ItemStackView stack={fromMaterialItem(change)} size={40} bare showTooltip />

                                        <div class={styles.changeItem}>
                                            <div class={styles.changeName}><MinecraftText text={change.displayName} /></div>
                                            <div class={styles.changeMeta}>
                                                <span class={`${styles.sideBadge} ${sideOf(change) === 'buy' ? styles.sideBuy : styles.sideSell}`}>
                                                    {sideOf(change) === 'buy' ? t('SHOP_STOCK_WATCH_SIDE_BUY') : t('SHOP_STOCK_WATCH_SIDE_SELL')}
                                                </span>
                                                <span class={styles.changeBot}>{change.botName}</span>
                                                <span class={styles.changeCb}>{getCitybuildLabel(change.citybuild)}</span>
                                                <Show when={change.amount > 0}>
                                                    <span class={styles.changeAmount}>{change.amount}x</span>
                                                </Show>
                                            </div>
                                        </div>

                                        <div class={styles.changePrices}>
                                            <div class={styles.priceCell}>
                                                <span class={styles.priceLabel}><Text val="SHOP_STOCK_WATCH_WAS" /></span>
                                                <span class={styles.priceValue}>
                                                    {change.oldPrice == null ? '-' : formatPrice(change.oldPrice)}
                                                </span>
                                            </div>
                                            <div class={styles.priceCell}>
                                                <span class={styles.priceLabel}><Text val="SHOP_STOCK_WATCH_NOW" /></span>
                                                <span class={`${styles.priceValue} ${kindClass(change.kind)}`}>
                                                    {change.newPrice == null ? '-' : formatPrice(change.newPrice)}
                                                </span>
                                            </div>
                                            <div class={styles.priceCell}>
                                                <span class={styles.priceLabel}><Text val="SHOP_STOCK_STOCK" /></span>
                                                <span class={styles.priceValue}>
                                                    {change.warehouseCount == null ? '-' : formatInteger(change.warehouseCount)}
                                                </span>
                                            </div>
                                        </div>

                                        <div class={styles.changeVerdict}>
                                            <span class={`${styles.kindBadge} ${kindClass(change.kind)}`}>{kindLabel(change.kind)}</span>
                                            <Show when={deltaPercent(change)}>
                                                {(delta) => <span class={`${styles.delta} ${kindClass(change.kind)}`}>{delta()}</span>}
                                            </Show>
                                        </div>

                                        <span class={styles.changeTime}><RelativeTime value={change.detectedAt} /></span>
                                    </div>
                                )}
                            </For>
                        </div>

                        <Show when={pageCount() > 1}>
                            <div class={styles.pager}>
                                <button
                                    class="btn btn-secondary btn-sm"
                                    disabled={page() <= 1}
                                    onClick={() => load(page() - 1)}
                                >
                                    <i class="fa-solid fa-chevron-left" /> <Text val="SHOP_STOCK_WATCH_NEWER" />
                                </button>
                                <span class={styles.pagerLabel}>{t('SHOP_STOCK_WATCH_PAGE_OF', page(), pageCount())}</span>
                                <button
                                    class="btn btn-secondary btn-sm"
                                    disabled={page() >= pageCount()}
                                    onClick={() => load(page() + 1)}
                                >
                                    <Text val="SHOP_STOCK_WATCH_OLDER" /> <i class="fa-solid fa-chevron-right" />
                                </button>
                            </div>
                        </Show>
                    </Show>

                    <h2 class={styles.sectionTitle}><Text val="SHOP_STOCK_WATCH_WATCHED" /></h2>
                    <div class={styles.botGrid}>
                        <For each={data().bots}>
                            {(bot) => (
                                <div class={`fcard ${styles.botCard}`}>
                                    <div class={styles.botCardHead}>
                                        <span class={styles.botName}>{bot.name}</span>
                                        <span class={styles.changeCb}>{getCitybuildLabel(bot.citybuild)}</span>
                                        <button
                                            class="btn btn-danger btn-sm btn-icon"
                                            title={t('SHOP_STOCK_WATCH_REMOVE')}
                                            onClick={() => removeWatch(bot)}
                                        >
                                            <i class="fa-solid fa-eye-slash" />
                                        </button>
                                    </div>
                                    <div class={styles.botCardRow}>
                                        <span class={styles.botCardLabel}><Text val="SHOP_STOCK_WATCH_ITEMS_STOCKED" /></span>
                                        <span>{bot.itemCount}</span>
                                    </div>
                                    <div class={styles.botCardRow}>
                                        <span class={styles.botCardLabel}><Text val="SHOP_STOCK_FETCHED" /></span>
                                        <RelativeTime value={bot.fetchedAt} />
                                    </div>
                                    <div class={styles.botCardRow}>
                                        <span class={styles.botCardLabel}><Text val="SHOP_STOCK_WATCH_NEXT_REFRESH" /></span>
                                        <Show when={bot.nextRefreshAtUtc} fallback={<span><Text val="SHOP_STOCK_WATCH_NEXT_REFRESH_SOON" /></span>}>
                                            <RelativeTime value={bot.nextRefreshAtUtc!} />
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
